/**
 * The song import window (2026-10-13, operator: "i need a better ui for importing and utlising mp3s in the
 * timeline. there needs to be a processing modal").
 *
 * Opens the moment a file is dropped or picked and stays over the timeline editor through the three stages:
 * UPLOAD (real bytes sent, from the browser's upload progress), ANALYSE (OPBridge's helper finding the beats, bar 1
 * and the drums: it reports no progress of its own, so this shows the time spent and the usual time, never a made-up
 * percentage) and READY (what was found: length, tempo with the half / double pick, bar 1, the whole song's
 * waveform), with the next step offered: mark the sections, or carry on. A failure says why and offers another file.
 * "Hide" keeps working while the analysis carries on; the song row shows where it is.
 *
 * Several files (the audio library, 2026-10-13: "if there a multiple, it'd be a modal queue"): a row per file with
 * where it is (waiting, uploading, queued or analysing, ready, failed), one at a time; click a row for its detail.
 */
import { createEffect, createSignal, on, onCleanup, Show, type JSX } from 'solid-js';
import type { Timeline } from '@opbuttons/schema';
import {
  audioQueue, beatmaps, importing, importModalOpen, importTrack, loadTrack, looksLikeAudio, patchBeatmap, peaksById, setImportModalOpen,
  type QueueItem,
} from '../timeline/testTrackStore.ts';
import { fitTimelineToSong, setTimelineTestTrack } from '../timeline/store.ts';
import { gridBpm, songBars, waveformPath } from '../timeline/testTrack.ts';
import { pushToast } from '../ui/toast.ts';

const fmtTime = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const fmtBytes = (n: number) => (n >= 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Import `file` and link the timeline to it once analysed (the window shows every stage). */
export async function importSongInto(timeline: Timeline, file: File): Promise<void> {
  const t = await importTrack(file, { timelineName: timeline.name });
  if (t && t.analysed && !t.analysisError) await linkSong(timeline.id, { id: t.id, name: t.name }, true);
}

/** Link a song to a timeline (imported, or picked from the library); once its beat map is in, a timeline shorter
 *  than the song grows to hold all of it (`fitTimelineToSong`), and says so. Picking the song it already has
 *  changes nothing, so a length set by hand stays. */
export async function linkSong(timelineId: string, track: { id: string; name: string }, reload = false): Promise<void> {
  const changed = setTimelineTestTrack(timelineId, track);
  await loadTrack(track.id, reload);
  if (!changed) return;
  const m = beatmaps()[track.id];
  const grown = m ? fitTimelineToSong(timelineId, songBars(m)) : null;
  if (grown) pushToast('ok', `The timeline is now ${grown} bars, to fit the whole song. Undo puts it back.`, 3500);
}

/** Where a queued file is, in a few words. */
function rowStatus(i: QueueItem, now: number): string {
  if (i.state === 'waiting') return 'waiting';
  if (i.state === 'uploading') return i.total ? `uploading ${Math.min(100, Math.round(((i.sent ?? 0) / i.total) * 100))}%` : 'uploading';
  if (i.state === 'analysing') return i.queued ? 'waiting to be analysed' : `analysing, ${Math.floor(Math.max(0, now - (i.analysisStartMs ?? now)) / 1000)} s`;
  if (i.state === 'done') return i.existing ? 'already in your library' : 'ready';
  return i.message ?? 'failed';
}

export function SongImportModal(props: {
  /** The timeline the import came from (its song row or panel); absent in the Audio section. */
  timeline?: Timeline;
  /** "Mark the sections" for the file just imported. */
  onMark: (trackId: string) => void;
}): JSX.Element {
  const [now, setNow] = createSignal(Date.now());
  const tick = setInterval(() => setNow(Date.now()), 250);
  onCleanup(() => clearInterval(tick));
  /** The row the operator clicked, else the file being worked on (or the last one). */
  const [pick, setPick] = createSignal<number | null>(null);
  const multi = () => audioQueue().length > 1;
  const imp = () => (pick() != null ? audioQueue().find((i) => i.key === pick()) : undefined) ?? importing();
  const busy = () => audioQueue().some((i) => i.state === 'waiting' || i.state === 'uploading' || i.state === 'analysing');
  const map = () => { const id = imp()?.trackId; return id ? beatmaps()[id] : undefined; };
  const peaks = () => { const id = imp()?.trackId; return id ? peaksById()[id] : undefined; };
  // The summary needs the beat map and peaks; ask for them the moment the analysis is done.
  createEffect(on(() => imp()?.state, (st) => { const id = imp()?.trackId; if (st === 'done' && id) void loadTrack(id, true); }));
  const uploadPct = () => { const i = imp(); return i?.total ? Math.min(100, Math.round(((i.sent ?? 0) / i.total) * 100)) : 0; };
  const analysedFor = () => { const i = imp(); return i?.analysisStartMs ? Math.max(0, (now() - i.analysisStartMs) / 1000) : 0; };
  const stage = () => imp()?.state ?? 'uploading';
  const stepState = (s: 'upload' | 'analyse' | 'ready'): 'done' | 'active' | 'todo' | 'failed' => {
    const st = stage();
    if (st === 'failed') return s === 'upload' && imp()?.trackId ? 'done' : s === (imp()?.trackId ? 'analyse' : 'upload') ? 'failed' : 'todo';
    const order = { waiting: -1, uploading: 0, analysing: 1, done: 2 } as const;
    const at = order[st as keyof typeof order]; const me = { upload: 0, analyse: 1, ready: 2 }[s];
    return me < at ? 'done' : me === at ? (st === 'done' ? 'done' : 'active') : 'todo';
  };
  let fileEl: HTMLInputElement | undefined;
  // Esc closes this window (hides it while it works), ahead of the editor's own Esc behind it.
  {
    const onKey = (e: KeyboardEvent) => {
      if (!importModalOpen() || !imp() || e.key !== 'Escape') return;
      e.preventDefault(); e.stopPropagation();
      setImportModalOpen(false);
    };
    window.addEventListener('keydown', onKey, { capture: true });
    onCleanup(() => window.removeEventListener('keydown', onKey, { capture: true }));
  }
  const W = 460; const H = 64;
  return (
    <Show when={importModalOpen() && imp()}>
      <div class="tl2-modal-back" data-testid="tl2-import" role="dialog" aria-modal="true" aria-label="Importing a song">
        <div class="tl2-modal">
          <div class="tl2-modal-h">
            <div>
              <div class="tl2-modal-t">{multi() ? (busy() ? `Importing ${audioQueue().length} files` : `${audioQueue().filter((i) => i.state === 'done').length} of ${audioQueue().length} files ready`) : stage() === 'done' ? (imp()!.existing ? `Already in your library as "${imp()!.name}"` : `${imp()!.name} is ready`) : stage() === 'failed' ? `${imp()!.name} did not import` : `Importing ${imp()!.name}`}</div>
              <div class="tl2-modal-sub" data-testid="tl2-import-sub">{stage() === 'done' && imp()!.existing
                ? `The same file is already there, so nothing was copied. Its marks and tempo are as you left them${imp()!.timelineName ? `, and it is linked to "${imp()!.timelineName}"` : ''}.`
                : stage() === 'done' && imp()!.timelineName
                ? `Linked to "${imp()!.timelineName}". Mark its sections to line the song up with them.`
                : stage() === 'done' ? 'In your Audio library, on every computer in the rig.'
                  : stage() === 'failed' ? 'Nothing was added.'
                    : 'OPBridge keeps it in your Audio library and plays it on the computer you choose.'}</div>
            </div>
            <button type="button" class="tl2-modal-x" aria-label="Close" onClick={() => setImportModalOpen(false)}>×</button>
          </div>

          <Show when={multi()}>
            <ul class="tl2-queue" data-testid="tl2-import-queue">
              {audioQueue().map((i) => (
                <li data-state={i.state} aria-current={(imp() as QueueItem | null | undefined)?.key === i.key ? 'true' : undefined} onClick={() => setPick(i.key)}>
                  <b>{i.name}</b>
                  <span>{rowStatus(i, now())}</span>
                  <Show when={i.state === 'uploading' && i.total}><i class="tl2-bar"><i style={{ width: `${Math.min(100, Math.round(((i.sent ?? 0) / (i.total ?? 1)) * 100))}%` }} /></i></Show>
                  <Show when={i.state === 'analysing' && !i.queued}><i class="tl2-bar tl2-bar-busy"><i /></i></Show>
                </li>
              ))}
            </ul>
          </Show>

          <ol class="tl2-steps">
            <li data-state={stepState('upload')}>
              <b>Upload</b>
              <Show when={stepState('upload') === 'active'} fallback={<span>{stepState('upload') === 'done' ? (imp()!.total ? fmtBytes(imp()!.total!) : 'sent') : ''}</span>}>
                <span>{imp()!.total ? `${fmtBytes(imp()!.sent ?? 0)} of ${fmtBytes(imp()!.total!)}` : 'starting...'}</span>
              </Show>
              <Show when={stepState('upload') === 'active'}><i class="tl2-bar"><i style={{ width: `${uploadPct()}%` }} /></i></Show>
            </li>
            <li data-state={stepState('analyse')}>
              <b>Analyse</b>
              <span>
                <Show when={stepState('analyse') === 'active'} fallback={stepState('analyse') === 'done' ? 'beats, bar 1 and drums found' : ''}>
                  {`${Math.floor(analysedFor())} s, usually 30 to 40 s`}
                </Show>
              </span>
              <Show when={stepState('analyse') === 'active'}><i class="tl2-bar tl2-bar-busy"><i /></i></Show>
              <Show when={stepState('analyse') === 'active'}>
                <div class="tl2-steps-note">Finding the beats and tempo, where bar 1 is, the kick, snare and hats, and where the singing is.</div>
              </Show>
            </li>
            <li data-state={stepState('ready')}><b>Ready</b><span /></li>
          </ol>

          <Show when={stage() === 'failed'}>
            <div class="tl2-modal-err" data-testid="tl2-import-error">{imp()!.message ?? 'The import failed.'}</div>
          </Show>

          <Show when={stage() === 'done' && map()}>
            <div class="tl2-sum" data-testid="tl2-import-summary">
              <svg class="tl2-sum-wave" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
                <path d={peaks() ? waveformPath(peaks()!, 0, map()!.duration_sec, W, H) : ''} />
                <line x1={(map()!.bar1_sec / map()!.duration_sec) * W} x2={(map()!.bar1_sec / map()!.duration_sec) * W} y1="0" y2={H} />
              </svg>
              <dl>
                <dt>Length</dt><dd>{fmtTime(map()!.duration_sec)}</dd>
                <dt>Tempo</dt>
                <dd>
                  <span class="tl2-seg">
                    <button type="button" aria-pressed={map()!.tempoChoice === 'half'} onClick={() => void patchBeatmap(imp()!.trackId!, { tempoChoice: 'half' })}>{(map()!.bpm / 2).toFixed(1)}</button>
                    <button type="button" aria-pressed={(map()!.tempoChoice ?? 'as-read') === 'as-read'} onClick={() => void patchBeatmap(imp()!.trackId!, { tempoChoice: 'as-read' })}>{map()!.bpm.toFixed(1)}</button>
                    <button type="button" aria-pressed={map()!.tempoChoice === 'double'} onClick={() => void patchBeatmap(imp()!.trackId!, { tempoChoice: 'double' })}>{(map()!.bpm * 2).toFixed(1)}</button>
                  </span>
                  <small> BPM: pick the one you play it at</small>
                </dd>
                <dt>Bar 1</dt><dd>{map()!.bar1_sec.toFixed(2)} s in{map()!.bar1_sec >= 1 ? ' (an intro before it)' : ''}</dd>
                <dt>Bars</dt><dd>{songBars(map()!)} at {gridBpm(map()!).toFixed(1)} BPM</dd>
                <dt>Sections</dt><dd>{(map()!.sections ?? []).length ? (map()!.sections ?? []).map((s) => s.name).join(', ') : 'none yet: the song plays straight through until you mark them'}</dd>
              </dl>
            </div>
          </Show>

          <div class="tl2-modal-acts">
            <Show when={busy()}>
              <button type="button" class="tl2-btn" onClick={() => setImportModalOpen(false)}>Hide (it carries on)</button>
            </Show>
            <Show when={stage() === 'failed'}>
              <label class="tl2-btn tl2-btn-primary">
                Choose another file...
                <input ref={fileEl} type="file" accept="audio/*,.mp3,.wav,.flac,.m4a,.aac,.aif,.aiff,.ogg,.opus" style={{ display: 'none' }}
                  onChange={(e) => { const f = e.currentTarget.files?.[0]; e.currentTarget.value = ''; if (f && looksLikeAudio(f) && props.timeline) void importSongInto(props.timeline, f); else if (f) void importTrack(f); }} />
              </label>
              <button type="button" class="tl2-btn" onClick={() => setImportModalOpen(false)}>Close</button>
            </Show>
            <Show when={stage() === 'done'}>
              <button type="button" class="tl2-btn tl2-btn-primary" data-testid="tl2-import-mark" onClick={() => { const id = imp()!.trackId; setImportModalOpen(false); if (id) props.onMark(id); }}>Mark the sections</button>
              <Show when={!busy()}><button type="button" class="tl2-btn" onClick={() => setImportModalOpen(false)}>Done</button></Show>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  );
}
